import {
  useDeleteMultipleSuppliers,
  useDeleteSupplier,
  useDuplicateSupplier,
  useGetCities,
  useGetCountries,
  useGetSuppliers,
} from '@/api/supplier.api';
import PlusIcon from '@/assets/icons/plus.svg?react';
import SearchIcon from '@/assets/icons/search.svg?react';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import StyledButton from '@/components/styled/StyledButton';
import StyledContainer from '@/components/styled/StyledContainer';
import StyledInput from '@/components/styled/StyledInput';
import StyledListHeader from '@/components/styled/StyledListHeader';
import StyledNextPreviousButtons from '@/components/styled/StyledNextPreviousButtons';
import StyledSelect from '@/components/styled/StyledSelect';
import StyledTable from '@/components/styled/StyledTable';
import ModalDeletion from '@/components/ui/ModalDeletion';
import ModalDuplication from '@/components/ui/ModalDuplication';
import { PAGE_SIZE } from '@/constants/pageSize';
import { PATHS } from '@/constants/Paths';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import useTableControls from '@/hooks/useTableControls';
import { supplierColumns } from '@/types/suppliers/supplierColumnDef';
import { suppliersListParsers } from '@/utils/searchParams/parsers';
import { RowSelectionState } from '@tanstack/react-table';
import { useQueryStates } from 'nuqs';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';

type SuppliersListFormValues = {
  search: string;
  countryName: { value?: string; label?: string } | undefined;
  cityName: { value?: string; label?: string } | undefined;
};

type SuppliersListUrlState = {
  name: string;
  country: string | null;
  city: string | null;
};

function formValuesToUrl(
  values: SuppliersListFormValues,
): SuppliersListUrlState {
  return {
    name: values.search,
    country: values.countryName?.value ?? null,
    city: values.cityName?.value ?? null,
  };
}

const ListSuppliersPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const getTranslatedConst = useTranslatedConst();
  const [urlFilters, setUrlFilters] = useQueryStates(suppliersListParsers);

  const formValues: SuppliersListFormValues = {
    search: urlFilters.name ?? '',
    countryName: urlFilters.country
      ? { value: urlFilters.country, label: urlFilters.country }
      : undefined,
    cityName: urlFilters.city
      ? { value: urlFilters.city, label: urlFilters.city }
      : undefined,
  };

  const { control, subscribe } = useForm<SuppliersListFormValues>({
    values: formValues,
  });

  useEffect(() => {
    const unsub = subscribe({
      formState: { values: true },
      callback: ({ values }) => {
        const next = formValuesToUrl(values as SuppliersListFormValues);
        setUrlFilters((prev) =>
          prev.country !== next.country ? { ...next, city: null } : next,
        );
      },
    });
    return () => unsub();
  }, [subscribe, setUrlFilters]);

  const { page, setPage, sorting, setSorting, sortField, sortOrder } =
    useTableControls();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [supplierToDeleteId, setSupplierToDeleteId] = useState<string[]>([]);
  const [supplierToDuplicateId, setSupplierToDuplicateId] = useState<string>();

  const debouncedName = useDebouncedSearch(urlFilters.name ?? '');

  const apiFilters = {
    name: debouncedName,
    country: urlFilters.country ?? '',
    city: urlFilters.city ?? '',
  };

  const selectedSuppliers = Object.keys(rowSelection);

  const { data: suppliersListResponse, isSuccess } = useGetSuppliers({
    page,
    pageSize: PAGE_SIZE,
    sortField,
    sortOrder,
    filters: apiFilters,
  });
  const { data: citiesOptions } = useGetCities({
    country: urlFilters.country ?? undefined,
    select: (data) => data?.map(({ city }) => ({ label: city, value: city })),
  });
  const { data: countriesOptions } = useGetCountries((data) =>
    data?.map(({ country }) => ({ label: country, value: country })),
  );
  const { mutateAsync: deleteSupplier } = useDeleteSupplier();
  const { mutateAsync: deleteMultipleSuppliers } = useDeleteMultipleSuppliers();
  const { mutateAsync: duplicateSupplier } = useDuplicateSupplier();

  return (
    <StyledContainer>
      <StyledListHeader
        title={t('suppliers.title')}
        subTitle={t('suppliers.subTitle')}
      >
        {selectedSuppliers.length > 0 && (
          <StyledButton
            title={`${t('suppliers.deleteSelection')} (${selectedSuppliers.length})`}
            className="font-semibold gap-1 px-3 py-2"
            onClick={() => setSupplierToDeleteId(selectedSuppliers)}
            icon={<TrashIcon className="w-5 h-5 stroke-brand-600" />}
            variant="outlined"
          />
        )}
        <StyledButton
          title={t('suppliers.addSupplier')}
          className="font-semibold gap-1 px-3 py-2"
          onClick={() =>
            navigate(PATHS.home.suppliers.create, {
              state: { listSearchParams: urlFilters },
            })
          }
          icon={<PlusIcon className="w-5 h-5 fill-white" />}
        />
      </StyledListHeader>
      <div className="flex flex-col w-full h-auto p-6 gap-6">
        <div className="flex flex-col gap-2">
          <span className="text-xs font-normal text-gray-600">
            {t('suppliers.filterBy')}
          </span>
          <div className="flex flex-col md:flex-row justify-between gap-4">
            <div className="flex flex-col md:flex-row gap-4">
              <StyledSelect
                className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                control={control}
                options={countriesOptions}
                name="countryName"
                placeholder={t('suppliers.country')}
                isClearable={true}
              />
              <StyledSelect
                className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                control={control}
                options={citiesOptions ?? []}
                name="cityName"
                isClearable={true}
                placeholder={t('suppliers.city')}
              />
            </div>
            <div className="w-full md:w-1/3 lg:w-1/4 2xl:w-1/5">
              <StyledInput
                control={control}
                name="search"
                placeholder={t('suppliers.quickSearch')}
                icon={<SearchIcon className="w-5 h-5 stroke-gray-500" />}
              />
            </div>
          </div>
        </div>
        <StyledTable
          columns={getTranslatedConst(supplierColumns)}
          data={suppliersListResponse?.suppliers ?? []}
          sorting={sorting}
          setSorting={setSorting}
          getRowId={(row) => row.id}
          setSelection={setRowSelection}
          onDeleteRow={(row) => {
            setSupplierToDeleteId([row.original.id]);
          }}
          onDuplicateRow={(row) => {
            setSupplierToDuplicateId(row.original.id);
          }}
          onViewRow={(row) =>
            navigate(PATHS.home.suppliers.list + `/${row.original.id}`, {
              state: { listSearchParams: urlFilters },
            })
          }
          onRowClick={(row) =>
            navigate(PATHS.home.suppliers.list + `/${row.original.id}`, {
              state: { listSearchParams: urlFilters },
            })
          }
        />
        {isSuccess && (
          <StyledNextPreviousButtons
            page={page}
            pageSize={PAGE_SIZE}
            setPage={setPage}
            dataLength={suppliersListResponse.total}
            label={t('suppliers.title').toLowerCase()}
          />
        )}
      </div>
      {supplierToDuplicateId && (
        <ModalDuplication
          actionOnClick={async (selectedFields) => {
            if (supplierToDuplicateId) {
              const duplicatedSupplier = await duplicateSupplier({
                supplierId: supplierToDuplicateId,
                fields: selectedFields.flatMap((fields) => fields.split(';')),
              });
              navigate(
                PATHS.home.suppliers.list + `/${duplicatedSupplier.data.id}`,
                { state: { listSearchParams: urlFilters } },
              );
            }
          }}
          closeModal={() => {
            setSupplierToDuplicateId(undefined);
          }}
          fields={[
            {
              label: t('suppliers.duplicationFields.requiredFields'),
              value: 'name;country;currency',
            },
            {
              label: t('suppliers.duplicationFields.administrativeInfo'),
              value: 'address;city;postalCode',
            },
            {
              label: t('suppliers.duplicationFields.contacts'),
              value: 'contacts',
            },
            {
              label: t('suppliers.duplicationFields.marginRate'),
              value: 'marginRate',
            },
          ]}
          code={
            suppliersListResponse?.suppliers.find(
              (supplier) => supplier.id === supplierToDuplicateId,
            )?.code ?? 0
          }
        />
      )}
      {supplierToDeleteId.length > 0 && (
        <ModalDeletion
          closeModal={() => {
            setSupplierToDeleteId([]);
          }}
          actionOnClick={() => {
            if (supplierToDeleteId.length === 1)
              deleteSupplier({ supplierId: supplierToDeleteId[0] });
            else {
              deleteMultipleSuppliers({ supplierIds: selectedSuppliers });
              setRowSelection({});
            }
          }}
          type="fournisseur"
          isMultiple={supplierToDeleteId.length > 1}
        />
      )}
    </StyledContainer>
  );
};

export default ListSuppliersPage;
